import { formatDateLong, type AmortizeRow, type Currency, type IsoDate } from '@koverta/shared';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Fmt } from '../lib/household';

/** One bar: a payment, or a whole year of them on long loans. */
interface Bucket {
  key: string;
  label: string;
  date: IsoDate;
  principal: number;
  interest: number;
  payment: number;
  /** Still owed after the bucket's last payment. */
  balance: number;
  /** Still owed in the what-if, when there is one. */
  alt: number | null;
  past: boolean;
}

const L = 58;
// Two panels on one x-axis: what is still owed, then each payment split into principal and interest.
const A = { top: 24, bottom: 160 };
const B = { top: 194, bottom: 290 };
const VH = 310;

/** The next round number at or above `v` (11.000 → 12.000, not 20.000), so the axis isn't half empty. */
function niceMax(v: number) {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  const step = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((k) => k * p >= v) ?? 10;
  return step * p;
}

function compact(minor: number, fmt: Fmt) {
  const v = minor / 100;
  const dec = fmt.locale === 'sr-Latn' ? ',' : '.';
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(2).replace('.', dec)} M`;
  if (Math.abs(v) >= 1000) return `${String(Math.round(v / 100) / 10).replace('.', dec)}k`;
  return fmt.num(minor);
}

function buckets(rows: AmortizeRow[], alt: AmortizeRow[] | null, today: IsoDate, fmt: Fmt): Bucket[] {
  const altAt = (date: IsoDate) => {
    if (!alt) return null;
    let b: number | null = null;
    for (const r of alt) if (r.date <= date) b = r.balance;
    return b ?? (alt[0] ? alt[0].balance + alt[0].principal : null);
  };
  // Monthly bars stay readable up to eight years; longer loans show one bar per year.
  if (rows.length <= 96)
    return rows.map((r) => ({
      key: r.date,
      label: formatDateLong(r.date, fmt.locale),
      date: r.date,
      principal: r.principal,
      interest: r.interest,
      payment: r.payment,
      balance: r.balance,
      alt: altAt(r.date),
      past: r.date <= today,
    }));
  const out: Bucket[] = [];
  for (const r of rows) {
    const y = r.date.slice(0, 4);
    const last = out[out.length - 1];
    if (last && last.key === y) {
      last.principal += r.principal;
      last.interest += r.interest;
      last.payment += r.payment;
      last.balance = r.balance;
      last.date = r.date;
      last.alt = altAt(r.date);
      last.past = r.date <= today;
    } else
      out.push({
        key: y,
        label: y,
        date: r.date,
        principal: r.principal,
        interest: r.interest,
        payment: r.payment,
        balance: r.balance,
        alt: altAt(r.date),
        past: r.date <= today,
      });
  }
  return out;
}

/**
 * Payoff chart (loans.md §4): still owed over time, and how each payment splits into principal and interest.
 * `rows` is the plan to draw; `alt` the what-if with extra payments. Past payments (whole-loan view) are muted.
 */
export function LoanChart({
  rows,
  alt,
  start,
  today,
  currency,
  fmt,
  title,
}: {
  rows: AmortizeRow[];
  alt: AmortizeRow[] | null;
  /** Owed before the first payment drawn. */
  start: number;
  today: IsoDate;
  currency: Currency;
  fmt: Fmt;
  title: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  // Draw at the real pixel width so text stays its size on wide cards and phones alike.
  const box = useRef<HTMLDivElement>(null);
  const [VW, setVW] = useState(600);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setVW(Math.max(320, Math.round(el.clientWidth)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const PW = VW - L - 10;
  const items = useMemo(() => buckets(rows, alt, today, fmt), [rows, alt, today, fmt]);
  const n = items.length;
  if (!n) return null;
  const money = (v: number) => fmt.money(v, currency, 2);
  const yearly = items[0]!.key.length === 4;
  const slot = PW / n;
  const barW = Math.max(1, slot - 2);
  const X = (i: number) => L + slot * i + slot / 2;
  const maxA = niceMax(Math.max(start, ...items.map((b) => b.balance)));
  const maxB = niceMax(Math.max(...items.map((b) => b.payment), 1));
  const YA = (v: number) => A.bottom - (v / maxA) * (A.bottom - A.top);
  const YB = (v: number) => B.bottom - (v / maxB) * (B.bottom - B.top);
  const owed = [`${L},${YA(start)}`, ...items.map((b, i) => `${X(i)},${YA(b.balance)}`)].join(' ');
  const area = `${L},${A.bottom} ${owed} ${X(n - 1)},${A.bottom}`;
  // The what-if line stops where it reaches zero.
  const altEnd = items.findIndex((b) => b.alt === 0);
  const altItems = alt ? items.slice(0, altEnd === -1 ? n : altEnd + 1) : [];
  const altLine = [`${L},${YA(start)}`, ...altItems.map((b, i) => `${X(i)},${YA(b.alt ?? 0)}`)].join(' ');
  const todayAt = items.findIndex((b) => !b.past);
  const showToday = todayAt > 0;
  const tickEvery = Math.max(1, Math.ceil(n / Math.max(3, Math.floor(PW / 70))));
  const tip = hover !== null ? items[hover] : null;
  const tipX = hover !== null ? X(hover) : 0;
  const round = (x: number, y: number, w: number, h: number) => {
    // Rounded top on the outer end of the stack, square on the baseline.
    const r = Math.min(4, w / 2, h);
    return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
  };

  return (
    <div className="loan-chart">
      <div className="loan-legend" aria-hidden="true">
        <span>
          <i className="lc-sw principal" /> Principal
        </span>
        <span>
          <i className="lc-sw interest" /> Interest
        </span>
        <span>
          <i className="lc-ln owed" /> Still owed
        </span>
        {alt ? (
          <span>
            <i className="lc-ln alt" /> With extra payments
          </span>
        ) : null}
      </div>
      <div
        ref={box}
        className="loan-plot"
        role="group"
        tabIndex={0}
        aria-label={`${title}. Use the arrow keys to read each ${yearly ? 'year' : 'payment'}.`}
        onMouseLeave={() => setHover(null)}
        onBlur={() => setHover(null)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const d = e.key === 'ArrowRight' ? 1 : -1;
            setHover((h) => Math.min(n - 1, Math.max(0, (h ?? (d > 0 ? -1 : n)) + d)));
          }
        }}
      >
        <svg width={VW} height={VH} viewBox={`0 0 ${VW} ${VH}`} role="img" aria-label={title}>
          {[0, 0.5, 1].map((k) => (
            <g key={k}>
              <line x1={L} x2={VW - 10} y1={YA(maxA * k)} y2={YA(maxA * k)} stroke="var(--line)" />
              <text x={L - 6} y={YA(maxA * k) + 4} textAnchor="end" fontSize="10" fill="var(--ink-3)">
                {compact(maxA * k, fmt)}
              </text>
              <line x1={L} x2={VW - 10} y1={YB(maxB * k)} y2={YB(maxB * k)} stroke="var(--line)" />
              <text x={L - 6} y={YB(maxB * k) + 4} textAnchor="end" fontSize="10" fill="var(--ink-3)">
                {compact(maxB * k, fmt)}
              </text>
            </g>
          ))}
          <text x={L} y={A.top - 8} fontSize="11" fill="var(--ink-2)">
            Still owed ({currency})
          </text>
          <text x={L} y={B.top - 8} fontSize="11" fill="var(--ink-2)">
            {yearly ? 'Paid each year' : 'Each payment'}: principal + interest
          </text>

          <polygon points={area} fill="var(--loan-owed-area)" />
          <polyline points={owed} fill="none" stroke="var(--loan-owed)" strokeWidth="2" />
          {alt ? (
            <polyline
              points={altLine}
              fill="none"
              stroke="var(--ink-2)"
              strokeWidth="2"
              strokeDasharray="5 4"
            />
          ) : null}

          {items.map((b, i) => {
            const x = X(i) - barW / 2;
            const hp = B.bottom - YB(b.principal);
            const hi = B.bottom - YB(b.interest);
            // 2px surface gap between the two segments; the interest segment carries the rounded end.
            const gap = hp > 0 && hi > 0 ? 2 : 0;
            return (
              <g key={b.key} opacity={b.past ? 0.45 : 1}>
                {hp > 0 ? (
                  hi > 0 ? (
                    <rect x={x} y={B.bottom - hp} width={barW} height={hp} fill="var(--loan-principal)" />
                  ) : (
                    <path d={round(x, B.bottom - hp, barW, hp)} fill="var(--loan-principal)" />
                  )
                ) : null}
                {hi > 0 ? (
                  <path d={round(x, B.bottom - hp - gap - hi, barW, hi)} fill="var(--loan-interest)" />
                ) : null}
              </g>
            );
          })}

          {showToday ? (
            <g>
              <line
                x1={L + slot * todayAt}
                x2={L + slot * todayAt}
                y1={A.top}
                y2={B.bottom}
                stroke="var(--ink-3)"
                strokeDasharray="2 3"
              />
              <text x={L + slot * todayAt + 4} y={A.top + 10} fontSize="10.5" fill="var(--ink-2)">
                Today
              </text>
            </g>
          ) : null}

          {tip && hover !== null ? (
            <>
              <line x1={tipX} x2={tipX} y1={A.top} y2={B.bottom} stroke="var(--ink-3)" strokeWidth="1" />
              <circle
                cx={tipX}
                cy={YA(tip.balance)}
                r="4"
                fill="var(--loan-owed)"
                stroke="var(--surface)"
                strokeWidth="2"
              />
              {tip.alt !== null && hover < altItems.length ? (
                <circle
                  cx={tipX}
                  cy={YA(tip.alt)}
                  r="4"
                  fill="var(--ink-2)"
                  stroke="var(--surface)"
                  strokeWidth="2"
                />
              ) : null}
            </>
          ) : null}

          {items.map((b, i) =>
            i % tickEvery === 0 ? (
              <text key={b.key} x={X(i)} y={VH - 4} textAnchor="middle" fontSize="10.5" fill="var(--ink-3)">
                {yearly ? b.key : `${b.date.slice(5, 7)}/${b.date.slice(2, 4)}`}
              </text>
            ) : null,
          )}
          {items.map((b, i) => (
            <rect
              key={b.key}
              x={L + slot * i}
              y={A.top}
              width={slot}
              height={B.bottom - A.top}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
            />
          ))}
        </svg>
        {tip ? (
          <div
            className="nw-tip"
            role="status"
            style={
              tipX > VW * 0.55
                ? { right: `${((VW - tipX + 10) / VW) * 100}%` }
                : { left: `${((tipX + 10) / VW) * 100}%` }
            }
          >
            <div className="h">
              {tip.label}
              {tip.past ? ' · paid' : ''}
            </div>
            <div className="row">
              <i className="lc-sw principal" />
              <span>Principal</span>
              <b>{money(tip.principal)}</b>
            </div>
            <div className="row">
              <i className="lc-sw interest" />
              <span>Interest</span>
              <b>{money(tip.interest)}</b>
            </div>
            <div className="row">
              <span />
              <span>{yearly ? 'Paid that year' : 'Payment'}</span>
              <b>{money(tip.payment)}</b>
            </div>
            <div className="row">
              <i className="nw-key" style={{ color: 'var(--loan-owed)' }} />
              <span>Still owed after</span>
              <b>{money(tip.balance)}</b>
            </div>
            {tip.alt !== null ? (
              <div className="row">
                <i className="nw-key dash" style={{ color: 'var(--ink-2)' }} />
                <span>With extra payments</span>
                <b>{money(tip.alt)}</b>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** The plan as a table (plan otplate): the chart's accessible view, and what to compare with the bank's. */
export function RepaymentTable({
  rows,
  today,
  currency,
  fmt,
}: {
  rows: AmortizeRow[];
  today: IsoDate;
  currency: Currency;
  fmt: Fmt;
}) {
  const money = (v: number) => fmt.money(v, currency, 2);
  return (
    <div className="loan-plan-wrap" tabIndex={0} role="region" aria-label="Repayment plan, scrollable">
      <table className="ivt loan-plan" aria-label="Repayment plan">
        <thead>
          <tr>
            <th>#</th>
            <th>Date</th>
            <th>Payment</th>
            <th>Interest</th>
            <th>Principal</th>
            <th>Still owed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.date} className={r.date <= today ? 'past' : ''}>
              <td>{i + 1}</td>
              <td>{formatDateLong(r.date, fmt.locale)}</td>
              <td>{money(r.payment)}</td>
              <td>{money(r.interest)}</td>
              <td>{money(r.principal)}</td>
              <td>{money(r.balance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
